import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Tooltip, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useTranslation } from 'react-i18next';

// Componente para renderizar filas de acciones
interface ActionRowProps {
  data: {
    id: number | null;
    name: string;
    responsability?: string;
    asociated_risk?: string;
    project_order?: number | null;
    consequenceId?: number | null;
    consequences?: Array<number | { id?: number | null }>;
    [key: string]: any;
  };
  onEdit: (type: string, record: any) => void;
  onDelete: (type: string, record: any) => void;
  editingItem: { type: string; id: number | null };
  editedFormValues: any;
  setEditedFormValues: any;
  modifyAction?: (actionId?: number | null) => void;
  setEditingItem: any;
  onMoveUp?: (id: number | null | undefined) => void;
  onMoveDown?: (id: number | null | undefined) => void;
  isFirst?: boolean;
  isLast?: boolean;
}

const ActionRow: React.FC<ActionRowProps> = ({
  data,
  onEdit,
  onDelete,
  editingItem,
  editedFormValues,
  setEditedFormValues,
  modifyAction,
  setEditingItem,
  onMoveUp,
  onMoveDown,
  isFirst = false,
  isLast = false,
}) => {
  const { t } = useTranslation();
  const { Text } = Typography;
  const normalizeId = (value: unknown): string | null => {
    if (value === null || value === undefined) {
      return null;
    }
    return String(value);
  };

  const editingItemId = normalizeId(editingItem.id);
  const dataId = normalizeId(data.id);

  const isEditing = editingItem.type === 'action' && editingItemId === dataId;

  const orderPrefix = data.project_order !== null && data.project_order !== undefined ? `${data.project_order}. ` : '';
  const formattedName = orderPrefix ? `${orderPrefix}${data.name ?? ''}`.trim() : data.name;
  const formattedEditingName = orderPrefix
    ? `${orderPrefix}${editedFormValues?.action?.name || data.name || ''}`.trim()
    : editedFormValues?.action?.name || data.name;

  const handleCancelEdit = () => {
    setEditingItem({ type: '', id: null });
    setEditedFormValues((prev: any) => ({
      ...prev,
      action: {
        id: undefined,
        name: '',
        responsability: '',
        asociated_risk: '',
        consequences: [],
        project_order: null,
      },
    }));
  };

  if (isEditing) {
    return (
      <div
        style={{
          marginBottom: '8px',
          padding: '8px',
          border: '1px solid #f0f0f0',
          display: 'flex',
          gap: 4,
          flexDirection: 'column',
        }}
      >
        {orderPrefix && <Text style={{ width: 200, display: 'block' }}>{formattedEditingName}</Text>}
        <TextArea
          value={editedFormValues.action.name}
          placeholder={t('Acción')}
          onChange={(e) =>
            setEditedFormValues((prev: any) => ({
              ...prev,
              action: { ...prev.action, name: e.target.value, id: data.id },
            }))
          }
          style={{ width: 200, display: 'block' }}
          size='small'
        />
        <small style={{ fontWeight: 'bold', marginTop: 20 }}>{t('Responsable')}</small>
        <TextArea
          value={editedFormValues.action.responsability}
          placeholder={t('Responsable (opcional)')}
          onChange={(e) =>
            setEditedFormValues((prev: any) => ({
              ...prev,
              action: { ...prev.action, responsability: e.target.value, id: data.id },
            }))
          }
          style={{ width: 200, display: 'block' }}
          size='small'
        />
        <small style={{ fontWeight: 'bold' }}>{t('Riesgo asociado')}</small>
        <TextArea
          value={editedFormValues.action.asociated_risk}
          placeholder={t('Riesgo asociado (opcional)')}
          onChange={(e) =>
            setEditedFormValues((prev: any) => ({
              ...prev,
              action: { ...prev.action, asociated_risk: e.target.value, id: data.id },
            }))
          }
          style={{ width: 200, display: 'block' }}
          size='small'
          autoFocus
        />
        <Button type='primary' onClick={() => modifyAction && modifyAction(data.id ?? null)}>
          {t('Guardar Cambios')}
        </Button>
        <Button onClick={handleCancelEdit}>{t('Cancelar')}</Button>
      </div>
    );
  }
  return (
    <div
      style={{
        marginBottom: '8px',
        padding: '8px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        gap: 4,
        flexDirection: 'column',
      }}
    >
      <Text style={{ width: 200, display: 'block' }}>{formattedName}</Text>
      {data.responsability && (
        <span>
          <Text type='secondary' style={{ width: 200, display: 'block' }}>
            <small style={{ fontWeight: 'bold' }}>{t('Responsable:')}</small>
            <br />
            {data.responsability}
          </Text>
        </span>
      )}
      {data.asociated_risk && (
        <span>
          <Text type='secondary' style={{ width: 200, display: 'block' }}>
            <small style={{ fontWeight: 'bold' }}>{t('Riesgo Asociado:')}</small>
            <br />
            {data.asociated_risk}
          </Text>
        </span>
      )}
      <span style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
        <Tooltip title={t('Subir recomendación')}>
          <Button
            icon={<ArrowUpOutlined />}
            onClick={() => onMoveUp?.(data.id)}
            size='small'
            type='text'
            disabled={isFirst || !onMoveUp}
            aria-label={t('Subir recomendación')}
          />
        </Tooltip>
        <Tooltip title={t('Bajar recomendación')}>
          <Button
            icon={<ArrowDownOutlined />}
            onClick={() => onMoveDown?.(data.id)}
            size='small'
            type='text'
            disabled={isLast || !onMoveDown}
            aria-label={t('Bajar recomendación')}
          />
        </Tooltip>
        <Tooltip title={t('Editar recomendación')}>
          <Button icon={<EditOutlined />} onClick={() => onEdit('action', data)} size='small' type='text' />
        </Tooltip>
        <Tooltip title={t('Eliminar recomendación')}>
          <Button
            icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
            onClick={() => onDelete('action', data)}
            size='small'
            type='text'
            danger
            aria-label={t('Eliminar recomendación')}
          />
        </Tooltip>
      </span>
    </div>
  );
};

export default ActionRow;
